/**
 * Decides whether an error response's own `message` is fit to show a customer,
 * and returns it cleaned up when it is.
 *
 * Both paths that can put backend text in front of a user read this: the toast
 * in AxiosWrapper (opt-in, per call) and the ApiError thrown by
 * unwrapApiResponse (whose `message` some call sites toast directly). Keeping
 * one implementation is the point — when they each had their own rule, the
 * wrapper sanitized and the unwrap path handed raw Joi output straight to the
 * driver, which is the THT-750 symptom the sanitizing was added to stop.
 *
 * Returns undefined when there is nothing worth showing, so the caller falls
 * back to its own copy.
 */

// The backend's HttpResponse builds every body message as
// `${statusText}${': ' + message}` — so a real reason arrives as
// "Bad Request: You have reached the maximum...", and a response with NO
// reason arrives as the bare status text ("Internal Server Error"). Both must
// be recognized: the prefix is stripped, and a bare status text is treated as
// "no message" so the caller's generic copy wins instead of a toast reading
// "Internal Server Error Please email support@tachusa.com...".
const HTTP_STATUS_TEXTS = new Set([
  'OK',
  'Created',
  'Accepted',
  'No Content',
  'Bad Request',
  'Unauthorized',
  'Forbidden',
  'Not Found',
  'Method Not Allowed',
  'Conflict',
  'Payload Too Large',
  'Too Many Requests',
  'Unprocessable Entity',
  'Internal Server Error',
  'Service Unavailable',
]);

// Joi renders every schema failure as `"<key>" <predicate>`, where the key is
// the raw request field name and the predicate can carry the pattern source
// verbatim — the toast a customer actually saw was `"emojiUnicode" with value
// "U+1F468 U+200D U+1F4BB" fails to match the required pattern:
// /^U\+[0-9A-F]{4,6}$/i`. Handlers return `error.message` unaltered in the 400
// body (see budget-items' `HttpResponse.badRequest(null, error?.message)`), so
// without this the message would go straight to the user.
//
// The quoted opener is the tell, narrowed to labels shaped like a *code*
// identifier — one unspaced token starting lowercase, which covers every
// request field the API validates (`emojiUnicode`, `customer_id`,
// `context.environment`, `items[0].name`). Requiring the lowercase start is
// what keeps a genuine message that opens by quoting the customer's own data
// ('"Fuel" is already used as a category name.') from being swallowed with it.
// With `abortEarly: false` Joi joins several failures with '. ' and the first
// segment still opens this way, so a multi-field rejection is caught too.
//
// A suppressed message costs only the caller's own copy showing instead, never
// a wrong sentence. Known limit: prose opening with a quoted lowercase word
// would be misread as a field label — no such message exists in the API today.
const FIELD_VALIDATION_MESSAGE = /^"[a-z_$][^"\s]*"\s/;

// Developer text that is NOT Joi's, so the shape above doesn't catch it.
//
// `nullish` is the load-bearing word: budget-items answers a missing id with
// three spellings of the same sentence — "Customer id or authentication id is
// nullish.", "Customer id or auth id is nullish.", "Customer id, category, or
// auth id is nullish." — and the term is a JavaScript one that appears in no
// copy written for a customer. Matching on it covers all three and whatever
// fourth variant gets written next. The second entry is API Gateway's own
// body, which never passes through HttpResponse at all.
//
// This is a stopgap, and a denylist can only ever suppress the shapes we have
// already seen. The durable fix is the inverse: the backend marking which
// reasons are customer-safe (CLAUDE.md already prescribes structured, tagged
// bodies for 409 conflicts) and this reading that marker instead of guessing
// from prose. Until then, every addition here is a shape that reached a
// customer once.
const DEVELOPER_MESSAGE = /\bnullish\b|^Missing Authentication Token$/i;

/**
 * Pulls the API's own explanation out of an error response body, following the
 * `payload.message` convention every HttpResponse body uses. Returns undefined
 * for a bodyless/HTML/blank response, for a message that carries no reason
 * beyond the HTTP status text, for raw schema-validation output, and for known
 * developer text — so the caller falls back to its generic copy.
 */
export const serverErrorMessage = (data: unknown): string | undefined => {
  if (typeof data !== 'object' || data === null) return undefined;
  const message = (data as { message?: unknown }).message;
  if (typeof message !== 'string') return undefined;

  const trimmed = message.trim();
  // Strip the "<status text>: " prefix only when it really is one, so a reason
  // that happens to contain a colon ("Limit: 5 categories") stays whole.
  const separator = trimmed.indexOf(': ');
  const detail =
    separator > 0 && HTTP_STATUS_TEXTS.has(trimmed.slice(0, separator))
      ? trimmed.slice(separator + 2).trim()
      : trimmed;

  if (!detail || HTTP_STATUS_TEXTS.has(detail)) return undefined;
  if (FIELD_VALIDATION_MESSAGE.test(detail)) return undefined;
  if (DEVELOPER_MESSAGE.test(detail)) return undefined;

  return detail;
};

/**
 * The raw `message` an error body carries, with no sanitizing at all. Kept for
 * diagnostics only — it goes to Sentry as `extra`, never in front of a user —
 * because the whole reason serverErrorMessage suppresses Joi output is that it
 * is the most useful text we get when something rejects and the least useful
 * text a driver can read.
 */
export const rawServerMessage = (data: unknown): string | undefined => {
  if (typeof data !== 'object' || data === null) return undefined;
  const message = (data as { message?: unknown }).message;
  return typeof message === 'string' ? message : undefined;
};
